import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from './Header';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { UnlockPaths } from './UnlockPaths';
import { CONTACTS } from '../config/brand';
import { Lock, Unlock, MessageCircle, Send, Sparkles, AlertTriangle, Gift, Copy, Check, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const isLockedStatus = (user) =>
    user?.role === 'student' && ['pending', 'rejected'].includes(user?.status);

export function CopyLinkButton({ url, testId, onTrack, label }) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        onTrack?.();
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            const ta = document.createElement('textarea');
            ta.value = url; document.body.appendChild(ta); ta.select();
            document.execCommand('copy'); document.body.removeChild(ta);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };
    return (
        <button type="button" onClick={copy} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md transition-colors"
            style={{ backgroundColor: 'var(--primary-muted)', color: 'var(--primary-color)', border: '1px solid var(--border-highlight)' }}
            data-testid={testId}>
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? t('locked.copied') : (label || t('locked.copyLink'))}
        </button>
    );
}

export function UnlockDialog({ open, onOpenChange }) {
    const { t } = useTranslation();
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl p-6 sm:p-8" style={{ backgroundColor: 'var(--background-surface)', border: '1px solid var(--border-highlight)', boxShadow: '0 0 80px rgba(255, 215, 0, 0.18), 0 30px 80px rgba(0,0,0,0.6)', backgroundImage: 'radial-gradient(ellipse at 50% -20%, rgba(255,215,0,0.16), transparent 55%)' }} data-testid="unlock-dialog">
                <DialogHeader>
                    <div className="icon-bubble-gold w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-3">
                        <Unlock className="w-8 h-8" style={{ color: '#FFD700' }} />
                    </div>
                    <DialogTitle className="text-center text-2xl sm:text-3xl gold-gradient-text tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {t('locked.dialogTitle')}
                    </DialogTitle>
                    <DialogDescription className="text-center">
                        {t('locked.dialogIntro')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 mt-1">
                    <UnlockPaths />

                    <div className="rounded-2xl p-4 text-center premium-card" data-testid="unlock-fasttrack">
                        <p className="text-sm font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{t('locked.fastTrackTitle')}</p>
                        <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>{t('locked.fastTrackText')}</p>
                        <div className="flex gap-2 justify-center">
                            <a href={`${CONTACTS.whatsapp.url}?text=${encodeURIComponent("Hi Nikayo 👋 I've completed my unlock step and uploaded my proof. Please confirm my training access. My email: ")}`} target="_blank" rel="noopener noreferrer" data-testid="unlock-whatsapp-btn">
                                <Button size="sm" className="bg-[#25D366] hover:bg-[#1fb958] text-white">
                                    <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
                                </Button>
                            </a>
                            <a href={CONTACTS.telegram.url} target="_blank" rel="noopener noreferrer" data-testid="unlock-telegram-btn">
                                <Button size="sm" variant="outline">
                                    <Send className="w-4 h-4 mr-1.5" /> {CONTACTS.telegram.handle}
                                </Button>
                            </a>
                        </div>
                    </div>
                    <p className="text-[11px] text-center" style={{ color: 'var(--text-muted)' }}>{t('locked.reviewNote')}</p>

                    <div className="rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.14), rgba(229,169,18,0.04))', border: '1px solid var(--border-highlight)' }} data-testid="unlock-welcome-package">
                        <div className="flex items-start gap-2.5">
                            <Gift className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#FFD700' }} />
                            <div>
                                <p className="text-sm font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{t('locked.welcomeTitle')}</p>
                                <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{t('locked.welcomeText')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function PendingBanner({ onUnlock, user }) {
    const { t } = useTranslation();
    if (user?.status === 'rejected') {
        return (
            <div className="rounded-2xl p-5 sm:p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                style={{ background: 'linear-gradient(120deg, rgba(220,38,38,0.10) 0%, rgba(220,38,38,0.04) 100%)', border: '1px solid rgba(220,38,38,0.4)' }}
                data-testid="rejected-banner">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#DC2626' }}>
                        <AlertTriangle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <p className="font-bold text-sm sm:text-base" style={{ color: 'var(--text-primary)' }}>{t('locked.rejectedTitle')}</p>
                        {user?.rejection_reason && (
                            <p className="text-xs sm:text-sm mt-0.5 font-semibold" style={{ color: '#FCA5A5' }} data-testid="rejected-reason">{user.rejection_reason}</p>
                        )}
                        <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{t('locked.rejectedText')}</p>
                    </div>
                </div>
                <Button onClick={onUnlock} className="btn-primary shrink-0" data-testid="pending-banner-unlock-btn">
                    <Unlock className="w-4 h-4 mr-2" />
                    {t('locked.reuploadCta')}
                </Button>
            </div>
        );
    }
    return (
        <div className="rounded-2xl p-5 sm:p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            style={{ background: 'linear-gradient(120deg, rgba(255,215,0,0.16) 0%, rgba(26,26,34,0.9) 60%, rgba(229,169,18,0.08) 100%)', border: '1px solid var(--border-highlight)', boxShadow: '0 0 40px rgba(255,215,0,0.12)' }}
            data-testid="pending-banner">
            <div className="flex items-start gap-3">
                <div className="icon-bubble-gold w-11 h-11 rounded-2xl flex items-center justify-center shrink-0">
                    <Sparkles className="w-5 h-5" style={{ color: '#FFD700' }} />
                </div>
                <div>
                    <p className="font-black text-base sm:text-lg gold-gradient-text" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('locked.bannerTitle')}</p>
                    <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{t('locked.bannerText')}</p>
                </div>
            </div>
            <Button onClick={onUnlock} className="btn-primary cta-pulse shrink-0 rounded-full px-7" data-testid="pending-banner-unlock-btn">
                <Unlock className="w-4 h-4 mr-2" />
                {t('locked.unlockCta')}
            </Button>
        </div>
    );
}

export function LockedFeaturePage({ featureKey }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            <main className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="locked-feature-page">
                <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 gold-glow" style={{ backgroundColor: 'rgba(255,184,0,0.12)', border: '1px solid rgba(184,134,11,0.4)' }}>
                    <Lock className="w-9 h-9" style={{ color: 'var(--primary-color)' }} />
                </div>
                <h1 className="text-2xl sm:text-3xl font-black mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                    {t(`locked.features.${featureKey}`)} — {t('locked.lockedTitle')}
                </h1>
                <p className="text-sm sm:text-base mb-8" style={{ color: 'var(--text-secondary)' }}>
                    {t('locked.lockedText')}
                </p>
                <Button onClick={() => setOpen(true)} className="btn-primary px-8 py-6 h-auto text-base" data-testid="locked-page-unlock-btn">
                    <Unlock className="w-5 h-5 mr-2" />
                    {t('locked.unlockCta')}
                </Button>
            </main>
            <UnlockDialog open={open} onOpenChange={setOpen} />
        </div>
    );
}

export function PendingGate({ featureKey, children }) {
    // Route-level gate: pending/rejected students see the locked page, everyone else passes through
    const { user } = useAuth();
    if (isLockedStatus(user)) {
        return <LockedFeaturePage featureKey={featureKey} />;
    }
    return children;
}

const TEASE_KEY = 'ptg_win_tease';
const TEASE_COOLDOWN_MS = 24 * 3600 * 1000;

export function WinTeasePopup({ onUnlock }) {
    const { t } = useTranslation();
    const [win, setWin] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const last = parseInt(localStorage.getItem(TEASE_KEY) || '0', 10);
        if (Date.now() - last < TEASE_COOLDOWN_MS) return;
        axios.get(`${API}/public/signals/recent`, { params: { limit: 10 } })
            .then((r) => {
                const w = (r.data || []).find((s) => s.status === 'tp_hit' && (s.rr_ratio || 0) > 0);
                if (w) { setWin(w); setOpen(true); }
            })
            .catch(() => {});
    }, []);

    const dismiss = () => {
        localStorage.setItem(TEASE_KEY, String(Date.now()));
        setOpen(false);
    };

    if (!win) return null;
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
            <DialogContent className="max-w-md" data-testid="win-tease-popup">
                <DialogHeader>
                    <p className="text-[11px] font-black tracking-[0.25em] uppercase text-center mb-1" style={{ color: 'var(--primary-color)' }}>
                        {t('locked.tease.label')}
                    </p>
                    <DialogTitle className="text-center text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {t('locked.tease.title')} 👀
                    </DialogTitle>
                    <DialogDescription className="text-center">
                        {t('locked.tease.text', { market: win.market })}
                    </DialogDescription>
                </DialogHeader>

                <div className="rounded-2xl p-5 text-center relative overflow-hidden" style={{ backgroundColor: '#0B0F14', border: '1px solid rgba(255,184,0,0.4)' }} data-testid="win-tease-card">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4" style={{ color: '#10B981' }} />
                        <span className="font-black text-sm text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{win.market}</span>
                        <span className="text-[10px] font-black px-2 py-0.5 rounded" style={{ backgroundColor: win.direction === 'BUY' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)', color: win.direction === 'BUY' ? '#10B981' : '#EF4444' }}>{win.direction}</span>
                    </div>
                    <p className="text-[10px] tracking-[0.25em] uppercase mb-1" style={{ color: 'rgba(255,255,255,0.45)' }}>{t('locked.tease.resultLabel')}</p>
                    <div className="relative inline-block select-none" aria-hidden="true">
                        <p className="text-5xl font-black" style={{ color: '#10B981', filter: 'blur(10px)', fontFamily: "'Outfit', sans-serif" }} data-testid="win-tease-blurred-rr">
                            +{Number(win.rr_ratio).toFixed(1)}R
                        </p>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255,184,0,0.9)' }}>
                                <Lock className="w-5 h-5" style={{ color: '#050505' }} />
                            </div>
                        </div>
                    </div>
                    <p className="text-[10px] mt-2 font-bold text-white flex items-center justify-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: '#10B981' }} /> TP HIT ✅
                    </p>
                </div>

                <div className="flex flex-col gap-2">
                    <Button className="btn-primary w-full" onClick={() => { dismiss(); onUnlock?.(); }} data-testid="win-tease-cta">
                        <Unlock className="w-4 h-4 mr-2" />
                        {t('locked.tease.cta')}
                    </Button>
                    <Button variant="ghost" className="w-full" onClick={dismiss} data-testid="win-tease-later">
                        {t('locked.tease.later')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
